/**
 * Inspection Findings & Actions Catalog (frontend mirror of backend).
 * Used by DeviceInspectionSheet to drive conditional UI.
 */

export const ACTIONS = {
    ninguna:                 "Ninguna acción",
    limpieza:                "Limpieza",
    limpieza_reactivacion:   "Limpieza y reactivación",
    trampa_nueva:            "Se colocó trampa nueva",
    limpieza_cebo_nuevo:     "Limpieza y colocación de cebo nuevo",
    cebadero_nuevo:          "Se colocó cebadero nuevo",
    reportar_reemplazo:      "Se reportó para envío de reemplazo",
    lamina_actual:           "Se dejó lámina actual",
    lamina_nueva:            "Se colocó lámina nueva",
    reportar_admin_foco:     "Reportar al admin (envío de foco UV nuevo)",
    reportar_admin_lampara:  "Reportar al admin (envío de técnico/repuesto)",
    retiro_reparacion:       "Retiro para reparación",
    solo_limpieza:           "Solo limpieza",
};

export const TRAP_FINDINGS_TREX_MULTI = [
    { v: "no_capture",           l: "Sin captura",                        actions: ["limpieza"] },
    { v: "with_capture",         l: "Con captura",                        actions: ["limpieza_reactivacion", "trampa_nueva"] },
    { v: "triggered_no_capture", l: "Trampa activada y sin captura",      actions: ["limpieza_reactivacion"] },
    { v: "damaged_by_client",    l: "Trampa dañada por cliente",          actions: ["trampa_nueva", "reportar_reemplazo"], alert: true },
    { v: "worn_out",             l: "Trampa en mal estado por uso",       actions: ["trampa_nueva", "reportar_reemplazo"], alertOnAction: ["reportar_reemplazo"] },
    { v: "inaccessible",         l: "Trampa inaccesible",                 actions: ["ninguna"] },
];

export const TRAP_FINDINGS_GATO_BANDEJA = [
    { v: "no_capture",        l: "Sin captura",                actions: ["ninguna"] },
    { v: "with_capture",      l: "Con captura",                actions: ["trampa_nueva"] },
    { v: "dirty_unusable",    l: "Trampa sucia / inservible",  actions: ["trampa_nueva"] },
    { v: "damaged_by_client", l: "Trampa dañada por cliente",  actions: ["trampa_nueva"], alert: true },
    { v: "with_insects",      l: "Trampa con insectos",        actions: ["trampa_nueva"], photo: true },
    { v: "inaccessible",      l: "Trampa inaccesible",         actions: ["ninguna"] },
];

export const BAIT_FINDINGS = [
    { v: "no_activity",            l: "Sin actividad",              actions: ["ninguna", "limpieza"] },
    { v: "missing",                l: "Cebo faltante",              actions: ["limpieza_cebo_nuevo"] },
    { v: "eaten",                  l: "Cebo comido",                actions: ["limpieza_cebo_nuevo"] },
    { v: "picked",                 l: "Cebo picado",                actions: ["ninguna", "solo_limpieza", "limpieza_cebo_nuevo"] },
    { v: "deteriorated",           l: "Cebo en mal estado",         actions: ["limpieza_cebo_nuevo"] },
    { v: "station_damaged_wear",   l: "Cebadero dañado por uso",    actions: ["cebadero_nuevo", "reportar_reemplazo"], alertOnAction: ["reportar_reemplazo"] },
    { v: "station_damaged_client", l: "Cebadero dañado por cliente", actions: ["cebadero_nuevo", "reportar_reemplazo"], alert: true },
    { v: "inaccessible",           l: "Cebadero inaccesible",       actions: ["ninguna"] },
];

export const LAMP_FINDINGS = [
    { v: "no_capture",      l: "Sin capturas",                            actions: ["ninguna"] },
    { v: "with_capture",    l: "Con capturas (insectos en lámina)",       actions: ["lamina_actual", "lamina_nueva"], showInsects: true },
    { v: "plate_saturated", l: "Lámina saturada (requiere cambio)",       actions: ["lamina_nueva"], showInsects: true },
    { v: "bulb_burnt",      l: "Foco UV fundido / sin luz",               actions: ["reportar_admin_foco"], alert: true },
    { v: "needs_repair",    l: "Lámpara requiere reparación",             actions: ["reportar_admin_lampara", "retiro_reparacion"], alert: true },
    { v: "inaccessible",    l: "Lámpara inaccesible",                     actions: ["ninguna"] },
];

export const UV_SPECIES = [
    { v: "mosca_domestica", l: "Mosca doméstica" },
    { v: "mosca_carne",     l: "Mosca de la carne" },
    { v: "mosca_drenaje",   l: "Mosca de drenaje" },
    { v: "mosca_botella",   l: "Mosca de la botella" },
    { v: "mosca_fruta",     l: "Mosca de la fruta" },
    { v: "mosquitos",       l: "Mosquitos" },
    { v: "palomilla",       l: "Palomilla" },
    { v: "gorgojo",         l: "Gorgojo" },
    { v: "escarabajo",      l: "Escarabajo" },
    { v: "otros_voladores", l: "Otros insectos voladores" },
];

export const UV_SPECIES_MAX = 3;
export const UV_SPECIES_LBL = Object.fromEntries(UV_SPECIES.map((s) => [s.v, s.l]));

/**
 * Returns the appropriate findings list for a given device + subtype.
 */
export function getFindings(deviceType, trapSubtype) {
    if (deviceType === "cebadero_cebo") return BAIT_FINDINGS;
    if (deviceType === "lampara_uv") return LAMP_FINDINGS;
    if (deviceType === "trampa") {
        if (trapSubtype === "trex" || trapSubtype === "multicaptura") return TRAP_FINDINGS_TREX_MULTI;
        if (trapSubtype === "gato_papel" || trapSubtype === "bandeja") return TRAP_FINDINGS_GATO_BANDEJA;
    }
    return [];
}

export function getFindingByCode(deviceType, trapSubtype, code) {
    return getFindings(deviceType, trapSubtype).find((f) => f.v === code) || null;
}

export function isAlertTriggered(finding, actionCode) {
    if (!finding) return false;
    if (finding.alert) return true;
    if (finding.alertOnAction?.includes(actionCode)) return true;
    return false;
}

/**
 * Map finding -> status color used by FloorPlanCanvas (statusByDeviceId).
 * Returns "ok" | "warning" | "alert" or null.
 */
export function findingToStatus(deviceType, trapSubtype, findingCode) {
    if (!findingCode) return null;
    if (deviceType === "trampa") {
        if (findingCode === "no_capture") return "ok";
        if (findingCode === "inaccessible") return "warning";
        if (findingCode === "with_capture" || findingCode === "with_insects") return "warning";
        return "alert"; // damaged, dirty, triggered, worn
    }
    if (deviceType === "cebadero_cebo") {
        if (findingCode === "no_activity") return "ok";
        if (findingCode === "picked") return "warning";
        if (findingCode === "inaccessible") return "warning";
        return "alert"; // missing/eaten/deteriorated/damaged
    }
    if (deviceType === "lampara_uv") {
        if (findingCode === "no_capture") return "ok";
        if (findingCode === "inaccessible") return "warning";
        if (findingCode === "with_capture") return "warning";
        return "alert"; // saturated/bulb/repair
    }
    return null;
}
